import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { Deal, User } from '../../types'
import { transitionDealStatus, DealStatus, statusLabel, statusColor } from '../../lib/dealStatusService'
import DealWorkspace from './DealWorkspace'
import './ContractsView.css'

/*
 * The contract-phase stages, taken from the canonical registry.
 *
 * This was a fourth hand-written copy of the status vocabulary, and it still
 * said "Under Offer" - the label Winnie renamed to Offer Accepted, because the
 * yard says the offer has been accepted, not that the car is under offer. It
 * also gave committed a colour of its own that no other screen used.
 *
 * Only `next` is this screen's own: it is the button on the card, not a property
 * of the status.
 */
const CONTRACT_STAGES: { id: string; label: string; color: string; next?: { id: string; label: string } }[] = [
  { id: 'under_offer', label: statusLabel('under_offer'), color: statusColor('under_offer'), next: { id: 'contract_signed', label: 'Move to Signed' } },
  { id: 'contract_signed', label: statusLabel('contract_signed'), color: statusColor('contract_signed'), next: { id: 'committed', label: 'Move to Committed' } },
  { id: 'committed', label: statusLabel('committed'), color: statusColor('committed'), next: { id: 'approved_for_payment', label: 'Move to Payment' } },
  { id: 'approved_for_payment', label: statusLabel('approved_for_payment'), color: statusColor('approved_for_payment') },
]

export default function ContractsView({ user }: { user: User }) {
  const [contracts, setContracts] = useState<Deal[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'date' | 'seller' | 'vin' | 'price'>('date')
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)
  const [banner, setBanner] = useState<string | null>(null)

  useEffect(() => {
    fetchContracts()
  }, [])

  const fetchContracts = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
      if (error) throw error
      setContracts(data || [])
    } catch (err) {
      console.error('Error fetching contracts:', err)
    } finally {
      setLoading(false)
    }
  }

  /* Agreed purchase price first (Winnie, 10 Oct): valuation_amount is the agreed buy price; market value is only a fallback. */
const dPrice = (d: Deal): number => d.valuation_amount || d.autograb_market_value || 0
  const dVin = (d: Deal) => d.vehicle_vin || d.autograb_vin || ''
  const dYear = (d: Deal) => d.autograb_year ?? d.vehicle_year
  const dMake = (d: Deal) => d.autograb_make || d.vehicle_make || ''
  const dModel = (d: Deal) => d.autograb_model || d.vehicle_model || ''
  const money = (n?: number) => (n != null ? `$${Math.round(n).toLocaleString('en-AU')}` : '—')

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return 'Not sent'
    const date = new Date(dateStr)
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24))
    if (days === 0) return 'today'
    if (days === 1) return 'yesterday'
    if (days < 7) return `${days} days ago`
    return date.toLocaleDateString('en-AU', { year: '2-digit', month: 'short', day: 'numeric' })
  }

  const getDDProgressPercent = (status?: string): number => {
    const map: Record<string, number> = {
      not_started: 0, in_progress: 25, submitted: 50, validated: 75, approved: 100, rejected: 0,
    }
    return map[status || 'not_started'] || 0
  }

  const handleStatusChange = async (deal: Deal, newStatus: string) => {
    setBanner(null)
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id
      const result = await transitionDealStatus(
        deal.id,
        deal.deal_status as DealStatus,
        newStatus as DealStatus,
        userId
      )
      setBanner(result.message)
      if (result.success) fetchContracts()
    } catch (err) {
      setBanner(err instanceof Error ? err.message : 'Could not change status')
      fetchContracts()
    }
  }

  const filterSort = (deals: Deal[]) => {
    let out = deals
    if (searchTerm) {
      const t = searchTerm.toLowerCase()
      out = out.filter(
        (d) =>
          d.seller_name?.toLowerCase().includes(t) ||
          dVin(d).toLowerCase().includes(t) ||
          `${dMake(d)} ${dModel(d)}`.toLowerCase().includes(t)
      )
    }
    return [...out].sort((a, b) => {
      switch (sortBy) {
        case 'seller':
          return (a.seller_name || '').localeCompare(b.seller_name || '')
        case 'vin':
          return dVin(a).localeCompare(dVin(b))
        case 'price':
          return (dPrice(b) || 0) - (dPrice(a) || 0)
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      }
    })
  }

  return (
    <div className="contracts-view">
      <div className="contracts-header">
        <h2>Purchase Details (Workspace)</h2>
        <div className="contracts-controls">
          <input
            type="text"
            placeholder="Search by seller, VIN, or vehicle"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="sort-select">
            <option value="date">Sort by Date</option>
            <option value="seller">Sort by Seller</option>
            <option value="vin">Sort by VIN</option>
            <option value="price">Sort by Price</option>
          </select>
        </div>
      </div>

      {banner && (
        <div className="contracts-banner" onClick={() => setBanner(null)}>
          {banner}
        </div>
      )}

      {loading ? (
        <p className="no-deals">Loading contracts...</p>
      ) : (
        <div className="contracts-statuses">
          {CONTRACT_STAGES.map((stage) => {
            const deals = filterSort(contracts.filter((d) => d.deal_status === stage.id))
            return (
              <div key={stage.id} className="status-section">
                <h3 className="status-title" style={{ color: stage.color }}>
                  {stage.label} ({deals.length})
                </h3>
                {deals.length === 0 ? (
                  <p className="no-deals">No deals in this status</p>
                ) : (
                  <div className="deals-list">
                    {deals.map((deal) => (
                      <div key={deal.id} className="deal-card">
                        <div className="deal-card-header">
                          <div className="deal-vehicle">
                            <h4>
                              {dYear(deal)} {dMake(deal)} {dModel(deal)}
                            </h4>
                            <p className="deal-price">{money(dPrice(deal))}</p>
                          </div>
                          <div className="deal-seller">
                            <p className="seller-name">{deal.seller_name}</p>
                            <p className="seller-contact">{deal.seller_phone}</p>
                          </div>
                        </div>

                        <div className="deal-card-details">
                          <span className="vin-badge">VIN: {dVin(deal) ? dVin(deal).substring(0, 10) + '…' : '—'}</span>
                          <span className="date-badge">
                            BOS {deal.bill_of_sale_sent_at ? `sent ${formatDate(deal.bill_of_sale_sent_at)}` : 'not sent'}
                          </span>
                        </div>

                        {stage.id === 'contract_signed' && (
                          <div className="deal-card-progress">
                            <div className="progress-bar">
                              <div className="progress-fill" style={{ width: `${getDDProgressPercent(deal.dd_status)}%` }} />
                            </div>
                            <p className="progress-text">DD Documents: {getDDProgressPercent(deal.dd_status)}%</p>
                          </div>
                        )}

                        <div className="deal-card-actions">
                          <button className="btn-secondary" onClick={() => setSelectedDeal(deal)}>
                            Open Workspace
                          </button>
                          {stage.next && (
                            <button className="btn-primary" onClick={() => handleStatusChange(deal, stage.next!.id)}>
                              {stage.next.label}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {selectedDeal && (
        <DealWorkspace
          user={user}
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onChanged={async () => {
            const id = selectedDeal.id
            const { data } = await supabase.from('deals').select('*').eq('id', id).maybeSingle()
            if (data) setSelectedDeal(data as Deal)
            fetchContracts()
          }}
        />
      )}
    </div>
  )
}
